// restrictionSentence — one restriction rule → the Georgian sentence an agent reads back.
//
// WHY THIS IS ITS OWN PURE MODULE (2026-08-04)
// The capture UI is direction B: the sentence IS the control. Its best property, and the
// reason it won over the bottom-sheet, is that "the preview and the editor are the same
// object, so they cannot disagree." That is a CORRECTNESS property and it only holds if
// the sentence is GENERATED from the predicate — never assembled per case in JSX. One
// function, one predicate, every case an output of the same rule.
//
// The matcher reads the same predicate through match-score.mjs's describePredicate(). If a
// case ever needs a special branch here to read well, the model is wrong, not the copy —
// say so rather than special-casing it.
//
// ⚠️ PURE AND DEPENDENCY-FREE. Imported by the Desk and (later) the public fill bundle;
// same contract as householdVocab.js and phoneRules.js.
//
// ── DECLENSION IS DATA, NOT AN ALGORITHM ────────────────────────────────────────
// Georgian needs inflected forms ("არ იღებს ძაღლს", "გარდა პატარისა"). core/georgian-stem.mjs
// solves the OTHER direction (inflected → stem, for matching) and is deferred with no
// consumer, so there is nothing to lean on. Migration 0402 therefore stores the forms:
// restriction_axis_value.name_ka_dat / name_ka_gen, ~47 values × 2, hand-written once.
//
// A MISSING FORM FALLS BACK TO THE NOMINATIVE AND FLAGS `complete: false`. It must NEVER
// guess an inflection: wrong Georgian in the one element whose entire job is being trusted
// is worse than plain Georgian. When `complete` is false the UI leads with `structured`,
// which is exactly what design direction A displayed — so the degradation lands on a shape
// that was already accepted rather than on something broken.

// ── WORD ORDER IS GRAMMAR, NOT DISPLAY (owner-ruled 2026-08-04) ─────────────────
// This list used to mirror restriction_axis.sort_order, which is the order the BUILDER shows
// axes in. It is not the order a Georgian sentence puts them in, and using it for both
// produced "არ იღებს ძაღლს პატარას" (modifier after its noun) and "არ იღებს ინდოელებს
// ქალებს" (every axis independently declined) — both reported `complete: true`, because the
// completeness flag only ever watched for a MISSING case form and has no opinion about a
// wrongly assembled phrase.
//
// The order below is the owner's: **Age → Nationality → Profession → head noun**, from
// "ახალგაზრდა ინდოელი სტუდენტი გოგო". The LAST axis present in a group is the head; every
// earlier one is a modifier. `composition` values (წყვილი, ოჯახი) are head nouns —
// "ინდოელი წყვილი" — so they sit after the member modifiers. `pet_size` precedes
// `pet_species` for the same reason: "პატარა ძაღლი", never "ძაღლი პატარა".
// `group_size` leads: a Georgian numeral stands before everything it counts — "2 ქართველი
// სტუდენტი ქალი". It is also the one axis whose values are quantities rather than
// descriptions, which is why it never becomes the head.
// `conduct` (core 1191, „მოწესრიგებული") is an adjective about the person, so it opens the
// description right after a count: "2 მოწესრიგებული სტუდენტი", "მოწესრიგებულ ოჯახს".
export const AXIS_ORDER = ['group_size', 'conduct', 'age_band', 'nationality', 'profession', 'sex',
  'composition', 'pet_size', 'pet_species', 'smoking', 'feature'];

// A predicate spanning subjects is not ONE noun phrase — "no Indian men with dogs" is a
// statement about people and a statement about an animal, and gluing them into
// "ინდოელ კაც ძაღლებს" would be nonsense. Axes are grouped by subject, each group builds its
// own phrase, and the groups are joined with `და`. The server's restriction_axis.subject_kind
// is authoritative; this map is the fallback for a vocabulary that has not loaded (or a test
// fixture that omits it), exactly like AXIS_KA below.
// ⚠ `composition` and `smoking` are PEOPLE here, whatever restriction_axis.subject_kind says.
// The database calls them `household` because that is what they attach to, and taking that
// literally split them into their own phrase: "დაიშვება ინდოელი და წყვილი". Owner-ruled
// 2026-08-04 — "მწეველი წყვილი" and "ინდოელი მწეველი" are one noun phrase, so they group with
// the member axes and the `და` disappears. `group_size` stays separate: it is a COUNT, not a
// describable person, and reads as its own clause.
const SUBJECT_KIND = {
  nationality: 'member', sex: 'member', age_band: 'member', profession: 'member',
  composition: 'member', smoking: 'member', group_size: 'member', conduct: 'member',
  feature: 'household',
  pet_species: 'pet', pet_size: 'pet',
};
const SUBJECT_ORDER = ['member', 'household', 'pet'];

// ── VALUES THAT ARE NOUNS AMONG ADJECTIVES ──────────────────────────────────────
// An axis sits at a fixed place in AXIS_ORDER, but a single VALUE can break the pattern.
// `age_band` is three adjectives (ახალგაზრდა, ზრდასრული, ხანდაზმული) and one absolute noun,
// `ბავშვი` — so the axis belongs early, in modifier position, EXCEPT when the child value is
// the one chosen. Owner-ruled: ბავშვი acts exactly like წყვილი, giving "ინდოელი ბავშვი"
// rather than "ბავშვი ინდოელი".
// 1219: „გარკვეული ეროვნების პირი" (nationalities not named) is a whole noun phrase, never an
// adjective in front of another noun.
const HEAD_VALUES = { age_band: ['child'], nationality: ['unspecified'] };
const forcesHead = (axis, spec) =>
  Array.isArray(spec) && spec.length > 0 && (HEAD_VALUES[axis] || []).length > 0
    && spec.every(v => HEAD_VALUES[axis].includes(v));

// Fallback axis labels — the server's restriction_axis.name_ka wins when present.
const AXIS_KA = {
  nationality: 'ეროვნება', sex: 'სქესი', age_band: 'ასაკი', profession: 'პროფესია',
  composition: 'შემადგენლობა', group_size: 'რაოდენობა', pet_species: 'ცხოველი',
  pet_size: 'ზომა', smoking: 'მოწევა', conduct: 'ხასიათი', feature: '—',
};

const POLARITY_KA = {
  exclude: 'არ იღებს', accept_only: 'იღებს მხოლოდ',
  prefer: 'სასურველია', allow: 'დაიშვება',
};

// 0927 — the owner refuses but will discuss it. Phrased to match fin_negotiable
// ('ფასზე მოლაპარაკება შესაძლებელია'), which agents already read on the price, so the same
// idea does not arrive in two different words on one card.
const NEGOTIABLE_KA = '— თუმცა მოლაპარაკება შესაძლებელია';

// The two verbs govern different cases, which is WHY exclusions read plural and permissions
// singular — `არ იღებს` is active and takes a dative object where the plural is natural
// ("არ იღებს ინდოელებს"); `დაიშვება` is passive and takes a nominative subject where the
// singular reads as a category rule ("დაიშვება ინდოელი ქალი"). Owner-confirmed 2026-08-04:
// the asymmetry is correct Georgian, not a bug to normalise away. (Plural permissions would
// need the verb to change too: `დაიშვებიან ინდოელი ქალები`.)
const CASE_OF_POLARITY = { exclude: 'dat', accept_only: 'dat', prefer: 'nom', allow: 'nom' };

// `vocab` shape (from GET /api/core/restriction-vocab):
//   { axes:   [{ code, name_ka, subject_kind, sort_order }],
//     values: [{ axis_code, value, name_ka, name_ka_dat, name_ka_gen, is_group }] }
// Omitted entirely → raw codes render, `complete` is false, and the caller shows the
// structured line. Never an error: a vocabulary that has not loaded is not a broken rule.
// ⚠ AN AXIS PRESENT WITH NO VALUES IS NOT AN AXIS. The exception builder keeps its parent's
// axes on screen even when the agent clears them, so a draft legitimately carries
// `{pet_species: [], pet_size: ['small']}`. Read literally that is a two-axis predicate, and
// it broke two things at once (2026-08-04): the phrase builder counted two axes and skipped
// the generic-noun injection, printing the bare adjective "დაიშვება პატარა"; and subsumes()
// found `[].every(…)` vacuously true, so it reported a valid exception where the narrow rule
// had stopped naming the set it was supposed to refine. Every reader strips them first.
function livePredicate(predicate) {
  const out = {};
  for (const [k, v] of Object.entries(predicate || {})) {
    if (Array.isArray(v) ? v.length > 0 : v != null) out[k] = v;
  }
  return out;
}

function valueEntry(vocab, axis, value) {
  return (vocab?.values || []).find(v => v.axis_code === axis && v.value === value) || null;
}
function axisLabel(vocab, axis) {
  const a = (vocab?.axes || []).find(x => x.code === axis);
  return a?.name_ka || AXIS_KA[axis] || axis;
}
// ⚠ THE MAP WINS OVER THE DATABASE, and that is not an oversight. `restriction_axis
// .subject_kind` answers "what does this axis attach to" — which the MATCHER needs, since a
// pet fact is evaluated against pets and a household fact against the household. Grouping
// words into a Georgian noun phrase is a different question with a different answer:
// `composition` and `smoking` attach to the household and are spoken about the people
// ("ინდოელი მწეველი", "სტუდენტი წყვილი"). Deferring to storage here produced "ინდოელი და
// წყვილი". The DB is consulted only for axes this map does not name, so a new axis still
// behaves sensibly without an edit here.
function subjectOf(vocab, axis) {
  if (SUBJECT_KIND[axis]) return SUBJECT_KIND[axis];
  const a = (vocab?.axes || []).find(x => x.code === axis);
  return a?.subject_kind || 'household';
}

// ── THE ATTRIBUTIVE (MODIFIER) FORM ─────────────────────────────────────────────
// A modifier standing before an INFLECTED head noun truncates: ინდოელი → ინდოელ,
// სტუდენტი → სტუდენტ, so "არ იღებს ინდოელ სტუდენტ გოგოებს". Leaving them in the nominative
// is ungrammatical. Before a NOMINATIVE head they do not truncate at all —
// "ინდოელი სტუდენტი გოგო" — which is why this is a function of the frame, not of the value.
//
// The rule is the owner's, stated with those two examples, and it is mechanical: drop a final
// `ი`. Values that do not end in one (პატარა, ახალგაზრდა, საშუალო, IT) are already
// attributive and pass through untouched. `name_ka_attr` is read FIRST so an irregular value
// can be corrected in data without touching this file — the same "declension is data"
// discipline as name_ka_dat/gen, just with a derivation that covers the regular case instead
// of a blank that degrades the whole sentence.
function attrText(e, value) {
  if (!e) return { text: String(value), exact: false };
  if (e.name_ka_attr) return { text: e.name_ka_attr, exact: true };
  const nom = e.name_ka || String(value);
  return { text: nom.endsWith('ი') ? nom.slice(0, -1) : nom, exact: !!e.name_ka };
}

// One value in the case a frame needs. Returns { text, exact } — `exact:false` means we
// fell back and the caller must not present the prose as authoritative.
function valueText(vocab, axis, value, form) {
  const e = valueEntry(vocab, axis, value);
  if (!e) return { text: String(value), exact: false };
  const inflected = form === 'gen' ? e.name_ka_gen : form === 'dat' ? e.name_ka_dat : null;
  if (form === 'nom') return { text: e.name_ka || String(value), exact: true };
  return inflected
    ? { text: inflected, exact: true }
    : { text: e.name_ka || String(value), exact: false };
}

// ── AFTER A NUMERAL, THE HEAD IS SINGULAR (B314, closed with the threshold ruling) ──
// "არ იღებს 2-ზე მეტ სტუდენტს", never "სტუდენტებს" — a counted noun does not pluralize in
// Georgian. 0402 stores ONE dative per value and it is the plural (right in every frame with
// no count in it), so the singular is DERIVED from the nominative: drop a final vowel, then
// +ს (dative) or +ისა (genitive). This is not a guess loose in the language — it is exactly
// the pattern every stored singular follows (ძაღლი→ძაღლს→ძაღლისა, კატა→კატას→კატისა,
// სხვა ცხოველი→სხვა ცხოველს→სხვა ცხოველისა), applied only when a count is present.
function singularText(e, value, form) {
  const nom = (e && e.name_ka) || String(value);
  if (form === 'nom') return { text: nom, exact: !!(e && e.name_ka) };
  const stem = /[იაე]$/.test(nom) ? nom.slice(0, -1) : nom;
  return { text: stem + (form === 'gen' ? 'ისა' : 'ს'), exact: !!(e && e.name_ka) };
}

// A range axis has no vocabulary — it reads as a number, which needs no declension.
function rangeText(axis, r) {
  const bits = [];
  if (r?.min != null && r?.max != null) return `${r.min}–${r.max}`;
  if (r?.min != null) bits.push(`${r.min}-დან`);
  if (r?.max != null) bits.push(`${r.max}-მდე`);
  return bits.join(' ') || '—';
}

// ── COUNTS ARE THRESHOLDS, NEVER SETS (owner-ruled 2026-08-04) ──────────────────
// "არ იღებს 2 სტუდენტს" is not a sentence anyone means: read literally it accepts 1, 3 and 5
// students and refuses exactly two. A count in a rule is always a bound — "მაქსიმუმ 2" or
// "2-ზე მეტი" — so a group_size value SET renders as the bound it encodes:
//   {1,2}        → მაქსიმუმ 2          (contiguous from 1 = a cap)
//   {3,4,5,6+}   → 2-ზე მეტი           (contiguous through the top = an excess)
//   {2}          → 2                    (literal; the UI never composes this shape)
// The underlying predicate stays an enum set so subsumes() needs no new machinery.
function countPhrase(vals) {
  const nums = [...new Set(vals.map(v => v === '6+' ? 6 : parseInt(v, 10)))].sort((a, b) => a - b);
  if (nums.some(isNaN)) return orJoin(vals);
  const contiguous = nums.every((n, i) => i === 0 || n === nums[i - 1] + 1);
  if (nums.length === 1) return String(vals[0]);
  if (contiguous && nums[0] === 1) return `მაქსიმუმ ${nums[nums.length - 1]}`;
  if (contiguous && vals.includes('6+')) return `${nums[0] - 1}-ზე მეტი`;
  return orJoin(vals);
}

// ── OR-JOINING A VALUE LIST (B325, 2026-08-05) ──────────────────────────────────
// Natural Georgian puts commas between list items and the conjunction only before the
// last: "ქართველებს, ინდოელებს ან რუსებს" — a repeated "ან" is logically exact but reads
// stiffly, and multi-value lists stopped being rare the moment agents began composing
// rules (0422 made the first). Two items keep the bare "ან"; one item is itself.
function orJoin(texts) {
  if (texts.length <= 2) return texts.join(' ან ');
  return `${texts.slice(0, -1).join(', ')} ან ${texts[texts.length - 1]}`;
}

// One axis's values, OR-joined. "OR prints as the word ან between values" — the designer's
// rule, and the reason no legend is needed. `role` is 'head' (this axis carries the case) or
// 'mod' (it stands before the head and takes the attributive form).
function axisPhrase(vocab, axis, spec, form, role = 'head') {
  if (axis === 'group_size' && Array.isArray(spec)) {
    // Always a modifier; in an inflected frame the trailing ი truncates like any other
    // attributive ("2-ზე მეტ სტუდენტს").
    const t = countPhrase(spec);
    const inflected = form === 'dat' || form === 'gen';
    return { text: inflected && t.endsWith('ი') ? t.slice(0, -1) : t, exact: true };
  }
  if (!Array.isArray(spec)) return { text: rangeText(axis, spec), exact: true };
  const inflected = form === 'dat' || form === 'gen';
  const parts = spec.map(v => (role === 'mod' && inflected)
    ? attrText(valueEntry(vocab, axis, v), v)
    // 'head-sg': the head of a COUNTED phrase — singular case (see singularText/B314).
    : role === 'head-sg'
      ? singularText(valueEntry(vocab, axis, v), v, form)
      : valueText(vocab, axis, v, role === 'mod' ? 'nom' : form));
  return { text: orJoin(parts.map(p => p.text)), exact: parts.every(p => p.exact) };
}

// ── A SIZE WITH NO ANIMAL HAS NO HEAD NOUN ──────────────────────────────────────
// `pet_size` alone produced "დაიშვება საშუალო" — an adjective with nothing to attach to,
// because the head of the pet phrase is normally the species. Georgian needs the generic
// noun, and needs `ზომის` with it: "საშუალო ზომის ცხოველი" / "არ იღებს საშუალო ზომის
// ცხოველს" (owner-ruled 2026-08-04). The dative is the owner's own; the genitive follows
// ძაღლი→ძაღლისა, which the vocabulary stores.
const PET_GENERIC = { nom: 'ცხოველი', dat: 'ცხოველს', gen: 'ცხოველისა' };

// ── VALUES THAT CAN NEVER BE THE HEAD ───────────────────────────────────────────
// `sex: other` reads "სხვა სქესის" — "of another sex", which qualifies a noun and cannot BE
// one. Alone it produced "ინდოელი სხვა", and even corrected it would dangle ("ინდოელი სხვა
// სქესის"). So it is modifier-only, and a generic person noun is supplied when nothing else
// can head the phrase: "სხვა სქესის პირი" (owner's own example).
// ⚠ The dative/genitive of პირი follow the ძაღლი→ძაღლს→ძაღლისა pattern the vocabulary
// already stores; only the nominative is owner-confirmed. Worth a second look.
const PERSON_GENERIC = { nom: 'პირი', dat: 'პირს', gen: 'პირისა' };
const MODIFIER_ONLY = { sex: ['other'] };
const isModifierOnly = (axis, spec) =>
  Array.isArray(spec) && spec.length > 0 && (MODIFIER_ONLY[axis] || []).length > 0
    && spec.every(v => MODIFIER_ONLY[axis].includes(v));

// One subject's axes → ONE noun phrase: modifiers first, head last, case on the head alone.
function subjectPhrase(vocab, predicate, axes, form) {
  // The sizes join FIRST and take one `ზომის` between them — "პატარა ან საშუალო ზომის
  // ცხოველს", not "პატარა ზომის ან საშუალო ზომის ცხოველს".
  if (axes.length === 1 && axes[0] === 'pet_size') {
    const sizes = axisPhrase(vocab, 'pet_size', predicate.pet_size, 'nom', 'mod');
    return { text: `${sizes.text} ზომის ${PET_GENERIC[form] || PET_GENERIC.nom}`, exact: sizes.exact };
  }
  // A noun value pulls its axis to the head slot (see HEAD_VALUES); everything else keeps
  // AXIS_ORDER. Relative order among the movers is preserved, so the sentence stays stable.
  let seq = axes.filter(a => !forcesHead(a, predicate[a]))
    .concat(axes.filter(a => forcesHead(a, predicate[a])));

  // Two kinds of axis can never END the phrase: a COUNT ("2" is not a person) and a
  // QUALIFIER like "სხვა სქესის". If any other axis is present it heads the phrase and the
  // qualifiers move to sit immediately before it — "ინდოელი სხვა სქესის სტუდენტი". Only when
  // nothing at all can head is the generic person noun supplied.
  // `conduct` (1191) is an adjective and stays where AXIS_ORDER puts it; alone it takes the
  // generic noun — "სასურველია მოწესრიგებული პირი".
  const canHead = a => a !== 'group_size' && a !== 'conduct' && !isModifierOnly(a, predicate[a]);
  const heads = seq.filter(canHead);
  const generic = heads.length === 0;
  if (!generic) {
    const head = heads[heads.length - 1];
    const quals = seq.filter(a => a !== head && isModifierOnly(a, predicate[a]));
    seq = seq.filter(a => a !== head && !quals.includes(a)).concat(quals, [head]);
  }

  // A counted noun is SINGULAR — "2-ზე მეტ სტუდენტს", never "სტუდენტებს". The stored
  // dative/genitive are the plural (right in every uncounted frame), so a phrase containing
  // group_size switches its head to the derived singular.
  const counted = seq.includes('group_size');
  const parts = seq.map((a, i) =>
    axisPhrase(vocab, a, predicate[a], form,
      (!generic && i === seq.length - 1) ? (counted ? 'head-sg' : 'head') : 'mod'));
  const text = parts.map(p => p.text).join(' ')
    + (generic ? ` ${PERSON_GENERIC[form] || PERSON_GENERIC.nom}` : '');
  return { text, exact: parts.every(p => p.exact) };
}

// Axes grouped by subject, each group already in AXIS_ORDER. Returns an array of arrays.
function subjectGroups(vocab, axes) {
  const by = new Map();
  for (const a of axes) {
    const k = subjectOf(vocab, a);
    if (!by.has(k)) by.set(k, []);
    by.get(k).push(a);
  }
  return [...by.entries()]
    .sort((x, y) => {
      const ix = SUBJECT_ORDER.indexOf(x[0]), iy = SUBJECT_ORDER.indexOf(y[0]);
      return (ix < 0 ? 99 : ix) - (iy < 0 ? 99 : iy);
    })
    .map(([, list]) => list);
}

// ── the sentence ────────────────────────────────────────────────────────────────
// Returns:
//   headline    — the prose an agent reads ("არ იღებს ინდოელებს კაცებს")
//   structured  — the unambiguous read-back, declension-free
//   untouched   — the axes this rule does NOT restrict, named (never left silent: the
//                 difference between "only girls" and "only Georgian girls" is the whole
//                 reason the flat `only` stance was wrong)
//   complete    — every value had the case form the frame needed
export function ruleSentence(rule, vocab, opts = {}) {
  const predicate = livePredicate(rule?.predicate);
  const axes = AXIS_ORDER.filter(a => a in predicate)
    .concat(Object.keys(predicate).filter(a => !AXIS_ORDER.includes(a)));
  const polarity = rule?.polarity || 'exclude';
  const form = CASE_OF_POLARITY[polarity] || 'dat';

  // One phrase per SUBJECT (modifiers → head, case on the head), joined by `და`. Within a
  // subject the words run together as one noun phrase; across subjects they are separate
  // statements and must not be glued.
  const groups = subjectGroups(vocab, axes);
  const phrases = groups.map(g => subjectPhrase(vocab, predicate, g, form));
  const complete = phrases.every(p => p.exact);

  const headline = `${POLARITY_KA[polarity] || polarity} ${phrases.map(p => p.text).join(' და ')}`.trim()
    // ⚠️ accept_only must SAY the part about everyone else. It is the only polarity that
    // makes a claim about people who were never named, and leaving that implicit is the
    // specific failure the brief called out. The tail agrees in CASE with the object it
    // follows — "სხვა არავინ" was nominative beside a dative and simply wrong (owner,
    // 2026-08-04); the comma form is the one chosen for the UI over the em-dash.
    + (polarity === 'accept_only' ? ', სხვას არავის' : '')
    // 0927: "no pets — but negotiable". An em-dash clause, not a comma, because it does not
    // extend the object list: it qualifies the whole refusal. It sits AFTER accept_only's
    // "სხვას არავის" tail, which belongs to the object, so the two never interleave.
    // ⚠️ Only ever appended to a refusal. On `allow`/`prefer` there is nothing to negotiate,
    // and "დაიშვება … თუმცა მოლაპარაკება შესაძლებელია" reads as though permission were in
    // doubt — the opposite of what the owner said.
    + (rule?.negotiable && (polarity === 'exclude' || polarity === 'accept_only')
        ? ` ${NEGOTIABLE_KA}` : '');

  // ⚠ THE FALLBACK KEEPS THE VERB (2026-08-04). It used to open with the axis label —
  // "ცხოველი კატა ან ძაღლი ან სხვა ცხოველი" — so the same subject produced a sentence in the
  // nominative frame ("დაიშვება კატა ან ძაღლი…") and a verbless fragment in the dative one,
  // side by side on the same card, purely because one value in the OR list has no stored
  // dative. A colon list after the verb needs no case agreement, so this stays honest about
  // the declension we do not have while still reading as the same kind of object as the
  // complete sentence.
  const structured = `${POLARITY_KA[polarity] || polarity}: ` + axes
    .map(a => `${axisLabel(vocab, a)} ${axisPhrase(vocab, a, predicate[a], 'nom').text}`)
    .join(' და ') + (axes.length > 1 ? ' — ერთდროულად' : '')
    // The fallback line carries it too. This is the line the UI leads with whenever a case
    // form is missing, so a negotiable rule whose vocabulary is incomplete must not silently
    // lose the one word that decides whether the flat is shown to the client at all.
    + (rule?.negotiable && (polarity === 'exclude' || polarity === 'accept_only')
        ? ` ${NEGOTIABLE_KA}` : '');

  // ── UNTOUCHED AXES: `accept_only` ONLY, and only within the rule's own subject ──────
  // This list exists for exactly one argument: "only girls" and "only Georgian girls" are
  // different rules, and the flat `only` stance could not tell them apart. That argument is
  // about `accept_only` and nothing else — it is the one polarity making a claim about people
  // who were never named. On "არ იღებს ძაღლს" the same line announces that nationality is
  // unrestricted, which no agent ever doubted, while being the longest thing on the card
  // (owner-ruled 2026-08-04).
  //
  // It is also scoped to the subjects the rule speaks about: a rule about people listing
  // `ცხოველი · ზომა` as unrestricted is mixing entities, not informing anyone.
  const shown = new Set(axes);
  const subjects = new Set(axes.map(a => subjectOf(vocab, a)));
  const untouched = polarity !== 'accept_only' ? [] : AXIS_ORDER
    .filter(a => a !== 'feature' && !shown.has(a))
    .filter(a => subjects.has(subjectOf(vocab, a)))
    .filter(a => !(opts.hideAxes || []).includes(a))
    .map(a => axisLabel(vocab, a));

  return { headline, structured, untouched, complete, polarity, axes };
}

// The exception clause of a pair (F19). Rendered as part of the SAME sentence, because
// "გარდა" is a word in it — which is what makes an exception impossible to author alone,
// the structural property that decided direction B over A.
export function exceptionClause(rule, vocab) {
  const predicate = livePredicate(rule?.predicate);
  const axes = AXIS_ORDER.filter(a => a in predicate);
  // Same phrase construction as the headline, in the genitive: modifiers attributive, the
  // case on the head alone — "გარდა პატარა ძაღლისა", not "გარდა ძაღლისა პატარისა".
  const phrases = subjectGroups(vocab, axes).map(g => subjectPhrase(vocab, predicate, g, 'gen'));
  return {
    text: `გარდა ${phrases.map(p => p.text).join(' და ')}`,
    complete: phrases.every(p => p.exact),
  };
}

// Does `narrow` carve out of `broad`? MIRROR of predicateSubsumes() in match-score.mjs —
// the UI must not promise a relationship the matcher will not honour.
//
// ⚠️ The two must stay in step. Specificity alone is NOT the test: "no dogs" + "Georgian
// women welcome" must not clear a Georgian woman's dog. The exception has to be a
// refinement of the very set the restriction describes — every axis the broad rule names,
// plus at least one more, with its values inside the broad rule's on each shared axis.
export function subsumes(broad, narrow, expansion = {}) {
  const b = livePredicate(broad?.predicate), n = livePredicate(narrow?.predicate);
  const bAxes = Object.keys(b);
  // MORE axes is one way to be narrower; it is not the only way (owner-ruled 2026-08-04).
  // "no pets, except a cat" is a tier-one case and used to be refused, because the exception
  // names the SAME single axis as its parent. What must hold is that the exception is
  // strictly narrower — never that it is more elaborate.
  if (Object.keys(n).length < bAxes.length) return false;
  let narrower = Object.keys(n).length > bAxes.length;
  for (const ax of bAxes) {
    const bv = b[ax], nv = n[ax];
    if (!Array.isArray(bv) || !Array.isArray(nv)) return false;   // ranges opt out
    const exp = expansion?.[ax] || {};
    const eq = (y, x) => String(y).toLowerCase() === String(x).toLowerCase();
    const inside = nv.every(x => bv.some(y => eq(y, x) || exp[y]?.[x] === 'definite'));
    if (!inside) return false;
    // Narrower on this axis means either fewer values (cat out of cat/dog/other) or a
    // DESCENT through the expansion (Indian out of foreigner) — the same value set at the
    // same breadth is not an exception, it is a restatement.
    const sameSet = nv.length === bv.length && nv.every(x => bv.some(y => eq(y, x)));
    if (!sameSet) narrower = true;
  }
  return narrower;
}


// ── is this rule the EXCEPTION half of that one? ────────────────────────────────────────────
// ⚠️ Lives here, beside `subsumes`, because two surfaces need the same answer and until
// 2026-09-02 only one of them had it. RestrictionRules.jsx defined this privately and used it to
// nest an exception under its parent; the card face (CooperationChips) had no access, so it
// flattened both rules into peer chips — and a parent plus its carve printed as a contradiction:
// "✗ cat, dog, other" beside "✓ small dog" reads as "no pets" and "pets fine" at once. Measured
// on the live corpus: 38 such pairs across 30 listings.
// 📌 The size took three attempts to get right, and the wrong ones are worth remembering: any
// opposite-polarity pair on one listing gave 742/828, same-AXIS pairs gave 240/132, and only
// running THIS predicate — polarity direction AND subsumption, with the expansion loaded — gave
// 38/30. A proxy for a rule is not the rule.

// The polarity an exception must carry to carve out of `parent`. They carve opposite ways: you
// narrow an "only X" by EXCLUDING part of X, and a "no X" by ALLOWING part of X.
export const exceptionPolarityOf = (parent) =>
  parent?.polarity === 'accept_only' ? 'exclude' : 'allow';

// Subsumption decides the SHAPE; the polarity decides the DIRECTION.
export function isExceptionOf(parent, other, expansion) {
  if (!other || other === parent) return false;
  const want = exceptionPolarityOf(parent);
  const ok = want === 'exclude'
    ? other.polarity === 'exclude'
    : (other.polarity === 'allow' || other.polarity === 'prefer');
  return ok && subsumes(parent, other, expansion);
}
